import { useCallback, useEffect, useRef, useState } from 'react';

interface UseResizableOptions {
  minHeight: number;
  maxHeightVh: number;
  initialHeightRem: number;
}

interface UseResizableResult {
  height: number;
  handleRef: React.RefObject<HTMLDivElement>;
  panelRef: React.RefObject<HTMLDivElement>;
}

function remToPx(rem: number): number {
  return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
}

export function useResizable(options: UseResizableOptions): UseResizableResult {
  const { minHeight, maxHeightVh, initialHeightRem } = options;

  const [height, setHeight] = useState(() => remToPx(initialHeightRem));
  const handleRef = useRef<HTMLDivElement>(null!);
  const panelRef = useRef<HTMLDivElement>(null!);
  const draggingRef = useRef(false);
  const startYRef = useRef(0);
  const startHeightRef = useRef(0);

  const onMouseMove = useCallback((e: MouseEvent) => {
    if (!draggingRef.current) {
      return;
    }
    const delta = startYRef.current - e.clientY;
    const maxHeight = window.innerHeight * (maxHeightVh / 100);
    const newHeight = Math.min(
      maxHeight,
      Math.max(minHeight, startHeightRef.current + delta),
    );
    setHeight(newHeight);
  }, [minHeight, maxHeightVh]);

  const onMouseUp = useCallback(() => {
    draggingRef.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    const handle = handleRef.current;
    if (!handle) {
      return;
    }

    const onMouseDown = (e: MouseEvent) => {
      draggingRef.current = true;
      startYRef.current = e.clientY;
      startHeightRef.current = height;
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
      e.preventDefault();
    };

    handle.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      handle.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [height, onMouseMove, onMouseUp]);

  return { height, handleRef, panelRef };
}
